import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  CopilotItemDto,
  CopilotSessionDto,
  CopilotViewDto,
  ProposalDto,
  outreachError,
} from '../../../data-transfer-objects/outreach.dto';
import { OutreachService } from '../../../services/outreach.service';
import { ProposalCardComponent } from '../proposal-card/proposal-card.component';

/** A message to drop into the composer; `at` makes the same text arrive twice. */
export interface CopilotPrefill {
  text: string;
  at: number;
}

/**
 * The copilot: a chat with the assistant that helps pick who Sharon talks to
 * and what to do with what she heard. It can only ever file proposals; each
 * one shows here as a card, and nothing happens until Approve.
 *
 * A turn can take a couple of minutes (it may run several tools), so the
 * composer is locked and says so while it works.
 */
@Component({
  selector: 'app-outreach-copilot',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatProgressSpinnerModule, ProposalCardComponent],
  templateUrl: './outreach-copilot.component.html',
  styleUrls: ['./outreach-copilot.component.scss'],
})
export class OutreachCopilotComponent implements OnInit, OnChanges {
  @Input() prefill: CopilotPrefill | null = null;
  @Input() available = true;

  sessions: CopilotSessionDto[] = [];
  view: CopilotViewDto | null = null;
  loading = true;
  working = false;
  error: string | null = null;
  text = '';

  constructor(private outreach: OutreachService) {}

  ngOnInit(): void {
    this.outreach.sessions().subscribe({
      next: ({ sessions }) => {
        this.sessions = sessions;
        if (sessions.length) this.open(sessions[0].id);
        else this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.error = outreachError(err, 'Could not load the copilot sessions');
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prefill'] && this.prefill) this.text = this.prefill.text;
  }

  open(id: number): void {
    this.loading = true;
    this.error = null;
    this.outreach.session(id).subscribe({
      next: (v) => {
        this.loading = false;
        this.view = v;
      },
      error: (err) => {
        this.loading = false;
        this.error = outreachError(err, 'Could not open the session');
      },
    });
  }

  newSession(): void {
    if (this.working) return;
    this.outreach.createSession().subscribe({
      next: ({ session }) => {
        this.sessions = [session, ...this.sessions];
        this.view = { session, items: [], proposals: [] };
      },
      error: (err) => (this.error = outreachError(err, 'Could not start a session')),
    });
  }

  send(): void {
    const text = this.text.trim();
    if (!text || this.working) return;
    this.working = true;
    this.error = null;
    const go = (sessionId: number) =>
      this.outreach.sendToCopilot(sessionId, text).subscribe({
        next: (res) => {
          this.working = false;
          this.text = '';
          this.view = res.view;
          this.sessions = [res.view.session, ...this.sessions.filter((s) => s.id !== sessionId)];
        },
        error: (err) => {
          this.working = false;
          this.error = outreachError(err, 'The copilot did not answer');
        },
      });
    if (this.view) {
      go(this.view.session.id);
      return;
    }
    this.outreach.createSession().subscribe({
      next: ({ session }) => {
        this.sessions = [session, ...this.sessions];
        this.view = { session, items: [], proposals: [] };
        go(session.id);
      },
      error: (err) => {
        this.working = false;
        this.error = outreachError(err, 'Could not start a session');
      },
    });
  }

  proposal(id: number): ProposalDto | undefined {
    return this.view?.proposals.find((p) => p.id === id);
  }

  /** Proposals of this session no stream item points at, so none goes unseen. */
  get loose(): ProposalDto[] {
    const shown = new Set(
      (this.view?.items ?? []).filter((i) => i.kind === 'proposal').map((i) => (i as { proposalId: number }).proposalId)
    );
    return (this.view?.proposals ?? []).filter((p) => !shown.has(p.id));
  }

  onProposalChanged(p: ProposalDto): void {
    if (!this.view) return;
    this.view = { ...this.view, proposals: this.view.proposals.map((x) => (x.id === p.id ? p : x)) };
  }

  toolLine(item: CopilotItemDto): string {
    if (item.kind !== 'tool') return '';
    const input = JSON.stringify(item.input ?? {});
    return `${item.name}(${input.length > 120 ? input.slice(0, 119) + '…' : input})`;
  }

  textOf(item: CopilotItemDto): string {
    return item.kind === 'admin' || item.kind === 'copilot' ? item.text : '';
  }

  proposalIdOf(item: CopilotItemDto): number {
    return item.kind === 'proposal' ? item.proposalId : 0;
  }
}
